import { AlertCircleIcon, HashIcon, LockIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { LoadMoreChannelsError } from '@/modules/slack-notification-channel/components/LoadMoreChannelsError';
import { useDeleteSlackNotificationChannelMutation } from '@/modules/slack-notification-channel/mutations/useDeleteSlackNotificationChannelMutation';
import { useSetSlackNotificationChannelMutation } from '@/modules/slack-notification-channel/mutations/useSetSlackNotificationChannelMutation';
import { useSelectableSlackChannelsQuery } from '@/modules/slack-notification-channel/queries/useSelectableSlackChannelsQuery';
import { useSlackNotificationChannelQuery } from '@/modules/slack-notification-channel/queries/useSlackNotificationChannelQuery';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import { Combobox } from '@/modules/ui/components/combobox/Combobox';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Label } from '@/modules/ui/components/label/Label';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { SavedIndicator } from '@/modules/ui/components/saved-indicator/SavedIndicator';
import { useSavedFlash } from '@/modules/ui/components/saved-indicator/useSavedFlash';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

// cmdk treats an empty string value as "always matches", so use an explicit sentinel.
const NONE_VALUE = '__none__';

const channelIcon = (isPrivate: boolean) =>
  isPrivate ? (
    <LockIcon className="size-4 shrink-0 opacity-60" />
  ) : (
    <HashIcon className="size-4 shrink-0 opacity-60" />
  );

interface SlackNotificationChannelFieldProps {
  /**
   * `stack` (default) is the recruiter card: label, caption, then the
   * picker. `row` is the portal settings row: label and hint on the left,
   * the picker on the right.
   */
  layout?: 'stack' | 'row';
}

/**
 * Lets a tenant member pick the shared Slack channel that hiring-manager
 * notifications are delivered to. Renders nothing unless the current user has a
 * connected Slack account and the `slack-notification-channel:read` permission;
 * the picker is read-only without `:write`. Saves on change: the picker shows
 * the saved channel, "Saved" confirms, and a failure stays under the picker.
 */
export function SlackNotificationChannelField({
  layout = 'stack',
}: SlackNotificationChannelFieldProps = {}) {
  const hasPermission = useCheckPermissions();
  const canRead = hasPermission([permissions.SlackNotificationChannel.Read]);
  const canWrite = hasPermission([permissions.SlackNotificationChannel.Write]);

  const currentQuery = useSlackNotificationChannelQuery({ enabled: canRead });
  const channelsQuery = useSelectableSlackChannelsQuery({ enabled: canRead });
  const setChannelMutation = useSetSlackNotificationChannelMutation();
  const clearChannelMutation = useDeleteSlackNotificationChannelMutation();
  const { isSaved, markSaved, clearSaved } = useSavedFlash();
  const [saveError, setSaveError] = useState<string | null>(null);

  const current = currentQuery.data ?? null;
  const isMutating =
    setChannelMutation.isPending || clearChannelMutation.isPending;

  const options = useMemo(() => {
    const channels = [...channelsQuery.channels].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true }),
    );
    const knownIds = new Set(channels.map((channel) => channel.id));

    return [
      { value: NONE_VALUE, label: 'Finlay app DM' },
      // Keep the current selection visible even if the picker list no longer
      // returns it (e.g. the channel was archived).
      ...(current && !knownIds.has(current.channelId)
        ? [
            {
              value: current.channelId,
              label: current.channelName,
              icon: channelIcon(false),
            },
          ]
        : []),
      ...channels.map((channel) => ({
        value: channel.id,
        label: channel.name,
        icon: channelIcon(channel.isPrivate),
      })),
    ];
  }, [channelsQuery.channels, current]);

  if (!canRead) {
    return null;
  }

  const selectedValue = current?.channelId ?? NONE_VALUE;

  const handleChange = (value: string) => {
    if (value === selectedValue) {
      return;
    }

    clearSaved();
    setSaveError(null);
    const feedback = {
      onSuccess: markSaved,
      onError: (error: Error) =>
        setSaveError(actionErrorMessage('change the channel', error)),
    };

    if (value === NONE_VALUE) {
      clearChannelMutation.mutate(undefined, feedback);
      return;
    }

    setChannelMutation.mutate({ channelId: value }, feedback);
  };

  const isRow = layout === 'row';

  return (
    <div
      className={
        isRow
          ? 'grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center'
          : 'space-y-2'
      }
    >
      <div className={isRow ? 'flex flex-col gap-0.5' : 'space-y-2'}>
        <div className="flex items-center gap-3">
          {isRow ? (
            <span className="text-[13px] font-medium">
              Notification channel
            </span>
          ) : (
            <Label>Notification channel</Label>
          )}
          <SavedIndicator isSaved={isSaved} />
        </div>
        {isRow ? (
          <span className="text-muted-foreground max-w-[480px] text-[13px] leading-[19px]">
            Introductions and scheduling messages go to this channel instead of
            the Finlay app DM. Applies to new messages only.
          </span>
        ) : (
          <Paragraph variant="caption" className="max-w-xl">
            Candidate introductions and Finlay&rsquo;s scheduling messages for
            hiring managers are posted here instead of the Finlay app DM.
            Applies to new messages only.
          </Paragraph>
        )}
      </div>

      {channelsQuery.isError ? (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>We couldn&rsquo;t load your Slack channels.</span>
            <Button
              variant="outline"
              size="sm"
              loading={channelsQuery.isFetching}
              onClick={channelsQuery.retry}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex flex-col gap-1.5">
          <Combobox
            aria-label="Notification channel"
            className={
              isRow ? 'h-8 w-full text-[13px] md:w-64' : 'w-full sm:max-w-sm'
            }
            value={selectedValue}
            options={options}
            disableSort
            disabled={
              !canWrite ||
              channelsQuery.isPending ||
              currentQuery.isLoading ||
              isMutating
            }
            placeholder={
              channelsQuery.isPending ? 'Loading channels…' : 'Select a channel'
            }
            searchPlaceholder="Search channels…"
            // Search only covers loaded pages; don't call a channel missing yet.
            emptyMessage={
              channelsQuery.isLoadingMore
                ? 'No match yet. Still loading channels…'
                : 'No channels found.'
            }
            onChange={handleChange}
          />
          {channelsQuery.hasLoadMoreError && (
            <LoadMoreChannelsError onRetry={channelsQuery.retry} />
          )}
          {saveError && (
            <InlineError className={isRow ? 'md:w-64' : undefined}>
              {saveError}
            </InlineError>
          )}
        </div>
      )}
    </div>
  );
}
